import { Badge, Button, Col, Row, Typography } from 'antd';
import React, { useCallback } from 'react';
import { useNotificationUpdateReadMutation } from 'api/notification';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { capitalizeWords } from 'utils/helpers';
import styles from 'components/common/notifiactionToaster/styles.module.scss';
import { NOTIFICATION_TYPE_MAPPING } from 'components';
import dayjs from 'dayjs';
import { ADMIN_ROUTES } from 'constants/routes';
import { useNavigate } from 'react-router-dom';
import { TypeOfNotificationEntityType } from 'models/Notification';
import { useLazyTaskByIdQuery } from 'api';

const { Text } = Typography;

interface INotificationCard {
  data: any;
  notificationRef?: (node: Element | null) => void;
}

export const NotificationCard = React.memo((props: INotificationCard) => {
  const { data, notificationRef } = props;
  const navigate = useNavigate();
  const { handleError } = useNotificationMessages();
  const [updateNotification] = useNotificationUpdateReadMutation();
  const [triggerTaskById] = useLazyTaskByIdQuery();

  const handleIsReadChange = useCallback(
    async (id: string, isRead: boolean) => {
      try {
        const { error } = await updateNotification({
          id,
          isRead: !isRead,
        });
        if (error) {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [handleError, updateNotification]
  );

  const onRedirectToEntity = useCallback(
    async (id: string, entityName: TypeOfNotificationEntityType) => {
      switch (entityName) {
        case 'BRAND':
          navigate(`${ADMIN_ROUTES.brand.view}/${id}/management`);
          return;
        case 'CLIENT':
          navigate(`${ADMIN_ROUTES.client}/${id}/general`);
          return;
        case 'LEAD':
          navigate(`${ADMIN_ROUTES.leads.view}/${id}/general`);
          return;
        case 'DEAL':
          navigate(`${ADMIN_ROUTES.deals.view}/${id}/purchase-request`);
          return;
        case 'TASK': {
          const { error }: any = await triggerTaskById({ id });
          const isTaskNotFound = error?.data?.data?.error_message?.includes('Task not found');
          if (isTaskNotFound) {
            handleError(error);
            return;
          }
          navigate(`${ADMIN_ROUTES.tasks.view}/${id}/general`);
          return;
        }
        default:
          return;
      }
    },
    [navigate, triggerTaskById, handleError]
  );

  return (
    <Col span={24} style={{ padding: '16px', borderRadius: '12px', border: '1px solid #CACFD8' }} ref={notificationRef}>
      <Row gutter={[0, 8]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'} wrap={false} gutter={4}>
            <Col>
              <Typography.Text>
                {data.user.firstName} {data.user.lastName} {data.action.toLowerCase()}{' '}
                {capitalizeWords(data.entityName)}{' '}
                <Typography.Text
                  ellipsis={{ tooltip: true }}
                  underline
                  className={styles.entity_id}
                  onClick={() => onRedirectToEntity(data.entityId, data.entityName)}
                >
                  {data.entityUuid}
                </Typography.Text>
                {NOTIFICATION_TYPE_MAPPING[data.action]}
              </Typography.Text>
            </Col>
            <Col flex="0 1 auto">{!data.read && <Badge color={'#FB3748'} />}</Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <Col>
              <Text type={'secondary'}>{dayjs(data.createDate).format('DD.MM.YYYY HH:mm')}</Text>
            </Col>
            <Col>
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                className={'underline-button'}
                onClick={() => handleIsReadChange(data.id, data.read)}
              >
                <Text underline style={{ color: '#05796B' }}>
                  {data.read ? 'Mark as Unread' : 'Mark as Read'}
                </Text>
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Col>
  );
});
